import React from 'react';
import { LegalPageShell, Section, Callout } from '../components/shared/LegalPageShell';

/**
 * SecurityPage — Stage 20B+ Wave A · /security
 *
 * Truthful, specific, non-hyperbolic. No "military-grade" language.
 * Explains what is and isn't in place, in a reassuring editorial tone.
 */
const SecurityPage = () => (
  <LegalPageShell
    eyebrow="Security"
    title="How we keep your health information safe."
    subtitle="We built Kindra with privacy and security as a first principle — not a feature we bolted on. This page is what that looks like in practice."
    updated="30 April 2026"
    current="/security"
  >
    <Section eyebrow="Infrastructure" title="Where your data lives.">
      <p>
        Your account and records are hosted on secure private infrastructure with modern industry-standard protections. We run our own servers behind a hardened firewall, keep the operating system continuously patched, and only the production application can access the database.
      </p>
    </Section>

    <Section eyebrow="In transit" title="Every connection is encrypted.">
      <p>
        Every request between your browser and Kindra is encrypted with TLS 1.2+ using modern cipher suites. We enforce HTTPS everywhere — insecure connections are rejected at the edge.
      </p>
    </Section>

    <Section eyebrow="At rest" title="Passwords and secrets.">
      <p>
        Your password is never stored in plain text. We hash it with bcrypt before it touches our database, so even we cannot read it. Payment details are handled entirely by Stripe — your card number never touches our servers.
      </p>
    </Section>

    <Callout testid="security-boundaries-callout">
      <p className="font-medium mb-3">Things we deliberately don't do:</p>
      <ul className="space-y-1.5 list-disc pl-5">
        <li>We don't sell your data. Not anonymised, not aggregated — not at all.</li>
        <li>We don't use your reports to train models that serve anyone else.</li>
        <li>We don't share your information with insurers, employers, or data brokers.</li>
        <li>We don't retain backups of deleted accounts beyond 30 days.</li>
      </ul>
    </Callout>

    <Section eyebrow="Access controls" title="Who can read what.">
      <p>
        Access to the production database is limited to the core team and is audit-logged. Routine product work (fixing bugs, adding features) is done against isolated test environments — never against real user records. We do not view individual user data unless you've explicitly asked us to (for example, to help diagnose a support issue).
      </p>
    </Section>

    <Section eyebrow="AI model handling" title="What the AI sees.">
      <p>
        When Kindra answers your question, it receives only the context needed for that specific answer — your relevant profile information, and the reports or notes tied to the question. The AI model does not retain anything after the response is returned. Your conversations do not become part of any external model's training set.
      </p>
    </Section>

    <Section eyebrow="Your responsibility" title="Keeping your account safe.">
      <p>
        Use a strong, unique password. Don't share your sign-in details. If you suspect someone else has accessed your account, change your password and email us immediately at hello@kindraai.dev — we can revoke all active sessions on request.
      </p>
    </Section>

    <Section title="What we're still building">
      <p>
        We're a small, focused team — we don't claim certifications we haven't earned. Two-factor authentication, a formal SOC 2 audit, and an enterprise-tier data export channel are on the near-term roadmap. We'd rather be honest about what's in place today than overclaim.
      </p>
    </Section>

    <Section title="Reporting a security issue">
      <p>
        Found a vulnerability? Email <a href="mailto:hello@kindraai.dev" className="underline underline-offset-2 hover:text-[#0E1C2E]" style={{ color: '#0E1C2E' }}>hello@kindraai.dev</a> with "Security" in the subject line. We take every report seriously and respond within two business days.
      </p>
    </Section>
  </LegalPageShell>
);

export default SecurityPage;
